import { Card } from "./Card";
import { BAUD_RATES, type FlashSettings } from "../flasher/flashService";
import type { FlashFreqValues, FlashModeValues, FlashSizeValues } from "esptool-js";

interface Props {
  settings: FlashSettings;
  openMonitorAfterFlash: boolean;
  disabled: boolean;
  onChange: (patch: Partial<FlashSettings>) => void;
  onOpenMonitorChange: (value: boolean) => void;
}

const FLASH_MODES: FlashModeValues[] = ["keep", "qio", "qout", "dio", "dout"];
const FLASH_FREQS: FlashFreqValues[] = ["keep", "80m", "40m", "26m", "20m"];
const FLASH_SIZES: FlashSizeValues[] = ["keep", "detect", "1MB", "2MB", "4MB", "8MB", "16MB", "32MB"];

export function OptionsCard({ settings, openMonitorAfterFlash, disabled, onChange, onOpenMonitorChange }: Props) {
  return (
    <Card
      step={3}
      title="Options"
      hint="The defaults are correct for Arduino exports. Change them only if you know you need to."
    >
      <div className="fields">
        <div className="field">
          <label className="field__label" htmlFor="baud">
            Baud rate
          </label>
          <select
            id="baud"
            value={settings.baudRate}
            disabled={disabled}
            onChange={(event) => onChange({ baudRate: Number(event.target.value) })}
          >
            {BAUD_RATES.map((rate) => (
              <option key={rate} value={rate}>
                {rate.toLocaleString()}
              </option>
            ))}
          </select>
          <span className="field__help">Drop to 115200 if writes fail or verification mismatches.</span>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="flash-mode">
            Flash mode
          </label>
          <select
            id="flash-mode"
            value={settings.flashMode}
            disabled={disabled}
            onChange={(event) => onChange({ flashMode: event.target.value as FlashModeValues })}
          >
            {FLASH_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="flash-freq">
            Flash frequency
          </label>
          <select
            id="flash-freq"
            value={settings.flashFreq}
            disabled={disabled}
            onChange={(event) => onChange({ flashFreq: event.target.value as FlashFreqValues })}
          >
            {FLASH_FREQS.map((freq) => (
              <option key={freq} value={freq}>
                {freq}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="flash-size">
            Flash size
          </label>
          <select
            id="flash-size"
            value={settings.flashSize}
            disabled={disabled}
            onChange={(event) => onChange({ flashSize: event.target.value as FlashSizeValues })}
          >
            {FLASH_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="field__help">
        <strong>keep</strong> writes the values the Arduino IDE already baked into your bootloader image. That is
        almost always what you want — overriding them is a common cause of a board that flashes fine but never boots.
      </p>

      <div className="divider" />

      <label className="toggle">
        <input
          type="checkbox"
          checked={settings.eraseAll}
          disabled={disabled}
          onChange={(event) => onChange({ eraseAll: event.target.checked })}
        />
        <span className="toggle__text">
          Erase entire flash first
          <div className="toggle__help">
            Wipes everything including saved Wi-Fi credentials and any NVS data. Needed when switching partition
            schemes or clearing a corrupted flash. Requires the bootloader and partition table to be included.
          </div>
        </span>
      </label>

      <label className="toggle">
        <input
          type="checkbox"
          checked={settings.compress}
          disabled={disabled}
          onChange={(event) => onChange({ compress: event.target.checked })}
        />
        <span className="toggle__text">
          Compress during transfer
          <div className="toggle__help">
            Substantially faster. Turn it off only if compressed writes fail on an unusual bridge.
          </div>
        </span>
      </label>

      <label className="toggle">
        <input
          type="checkbox"
          checked={openMonitorAfterFlash}
          disabled={disabled}
          onChange={(event) => onOpenMonitorChange(event.target.checked)}
        />
        <span className="toggle__text">
          Open the serial monitor after flashing
          <div className="toggle__help">
            Reopens the port at 115200 baud so you can watch the board boot and confirm the firmware runs.
          </div>
        </span>
      </label>
    </Card>
  );
}
